import { useState, useEffect, useMemo, useCallback } from 'react';

import { Prize, ProductOrPrize, StoredProductData, GameState, AppState } from '../types/game';
import { Product } from './useProducts';
import { distributeItemsEvenly } from '../utils/GameUtils';
import { getNumberOfPrizes, getPrizeAmount, MAX_PRODUCTS_FOR_GAME } from '../constants/gameConfig';
import { ScrollCollectSettingsResponse } from '../services/types/api';
import { useAppSelector } from '../store/hooks';
import { selectSelectedProduct } from '../store/selectors';
import diamondImage from '../assets/diamond.png';

export const useGameState = (
  fetchedProducts: Product[],
  loadingProducts: boolean,
  settings?: ScrollCollectSettingsResponse | null
) => {
  const [items, setItems] = useState<ProductOrPrize[]>([]);
  const [loading, setLoading] = useState(true);
  const [gameState, setGameState] = useState<GameState | null>(null);

  const [claimedPrizes, setClaimedPrizes] = useState<number>(0);

  const [walletAmount, setWalletAmount] = useState<number>(0);

  const [claimedPrizeIds, setClaimedPrizeIds] = useState<string[]>([]);

  const [appState, setAppState] = useState<AppState>('collapsed');

  // Read selectedProduct from Redux store instead of local state
  const selectedProduct = useAppSelector(selectSelectedProduct);
  const [localSelectedProduct, setLocalSelectedProduct] = useState<Product | null>(null);

  // Get dynamic values from settings
  const numberOfPrizes = getNumberOfPrizes(settings);
  const prizeAmount = getPrizeAmount(settings);

  // Use Redux selectedProduct if available, otherwise fall back to local state
  const currentSelectedProduct = selectedProduct || localSelectedProduct;

  // Effect: Save Game State whenever relevant state changes
  useEffect(() => {
    if (!loading) {
      // Only save when not in loading state
      const updatedGameState: GameState = {
        claimedPrizes,
        walletAmount,
        claimedPrizeIds,
        selectedProductId: currentSelectedProduct?.id,
        appState,
        distributedItems: items.map((item) => ({
          type: item.type,
          id: item.item.id,
          claimed: item.type === 'prize' ? item.item.claimed : undefined,
        })),
      };
      setGameState(updatedGameState);
    }
  }, [claimedPrizes, walletAmount, claimedPrizeIds, currentSelectedProduct, appState, items, loading]);

  // Effect: Initialize or restore game elements
  useEffect(() => {
    if (loadingProducts || fetchedProducts.length === 0) {
      return;
    }

    const setupGameElements = async () => {
      setLoading(true);
      try {
        const savedState = gameState;
        const hasSavedDistribution = savedState?.distributedItems && savedState.distributedItems.length > 0;

        // Create current prizes array with proper claimed status
        const currentPrizes: Prize[] = Array.from({ length: numberOfPrizes }, (_, i) => ({
          id: `prize-${i + 1}`,
          title: `Collect €${prizeAmount}`,
          description: '',
          imageUrl: diamondImage,
          claimed: claimedPrizeIds.includes(`prize-${i + 1}`),
        }));

        // Get target product set
        const targetProductSet =
          fetchedProducts.length > MAX_PRODUCTS_FOR_GAME
            ? [...fetchedProducts]
                .sort((a, b) => String(a.id).localeCompare(String(b.id)))
                .slice(0, MAX_PRODUCTS_FOR_GAME)
            : [...fetchedProducts];

        let newDistributedItems: ProductOrPrize[];

        if (hasSavedDistribution) {
          // Restore saved distribution
          newDistributedItems = savedState!
            .distributedItems!.map((item) => {
              if (item.type === 'prize') {
                const prizeDetail = currentPrizes.find((p) => p.id === item.id);
                return prizeDetail
                  ? {
                      type: 'prize' as const,
                      item: {
                        ...prizeDetail,
                        claimed: claimedPrizeIds.includes(prizeDetail.id),
                      },
                    }
                  : null;
              } else {
                const productDetail = fetchedProducts.find((p) => p.id === item.id);
                return productDetail
                  ? {
                      type: 'product' as const,
                      item: productDetail,
                    }
                  : null;
              }
            })
            .filter((item): item is ProductOrPrize => item !== null);

          // Verify distribution integrity
          if (newDistributedItems.length !== targetProductSet.length + numberOfPrizes) {
            // Distribution is invalid, create new one
            newDistributedItems = distributeItemsEvenly(targetProductSet, currentPrizes);
          }
        } else {
          // Create new distribution
          newDistributedItems = distributeItemsEvenly(targetProductSet, currentPrizes);
        }

        // Update state
        setItems(newDistributedItems);

        // Save new distribution and update game state in one go
        const updatedGameState: GameState = {
          claimedPrizes,
          walletAmount,
          claimedPrizeIds,
          selectedProductId: currentSelectedProduct?.id,
          appState,
          distributedItems: newDistributedItems.map((item) => ({
            type: item.type,
            id: item.item.id,
            claimed: item.type === 'prize' ? item.item.claimed : undefined,
          })),
        };
        setGameState(updatedGameState);
      } catch (error) {
        console.error('Error setting up game elements:', error);
      } finally {
        setLoading(false);
      }
    };

    setupGameElements();
  }, [fetchedProducts, loadingProducts, numberOfPrizes, prizeAmount]);

  // Effect: Restore Selected Product from localStorage
  useEffect(() => {
    if (fetchedProducts.length === 0 || loadingProducts) return;

    const storedProductDataJSON = localStorage.getItem('selectedProductData');
    const currentGameState = gameState;
    const savedProductIdFromGameState = currentGameState?.selectedProductId;

    let productToSelect: Product | null = null;

    if (storedProductDataJSON) {
      try {
        const storedProduct: StoredProductData = JSON.parse(storedProductDataJSON);
        const matchingProduct = fetchedProducts.find((p) => p.id === storedProduct.id);

        if (matchingProduct) {
          productToSelect = matchingProduct;
        } else {
          // Create a complete Product object if not found in fetched list (e.g. from direct link)
          productToSelect = {
            id: storedProduct.id,
            title: storedProduct.title,
            handle: 'restored-handle',
            description: '',
            descriptionHtml: '',
            variants: {
              edges: [
                {
                  node: {
                    id: `${storedProduct.id}-variant`,
                    title: 'Default',
                    price: String(storedProduct.price),
                    sku: '',
                    position: 1,
                    availableForSale: true,
                    inventoryQuantity: 1,
                  },
                },
              ],
            },
            options: [],
            media: {
              edges: [],
            },
            other_colors: null,
            metafields: {
              edges: [],
            },
            priceRange: {
              minVariantPrice: {
                amount: String(storedProduct.price),
                currencyCode: storedProduct.currencyCode || 'EUR',
              },
            },
            featuredImage: {
              url: storedProduct.image,
              altText: storedProduct.title,
            },
          };
        }

        if (productToSelect) {
          setLocalSelectedProduct(productToSelect);
          // Update game state with this selected product
          const existingState: GameState = {
            claimedPrizes: 0,
            walletAmount: 0,
            claimedPrizeIds: [],
            appState: 'collapsed',
          };
          setGameState({ ...existingState, selectedProductId: productToSelect.id });
        }
        localStorage.removeItem('selectedProductData'); // Clear after processing
      } catch (error) {
        console.error('Error parsing stored product data in useGameState:', error);
        localStorage.removeItem('selectedProductData'); // Clear if error
      }
    } else if (savedProductIdFromGameState) {
      // If no direct stored data, but game state has a selected product ID
      const matchingProduct = fetchedProducts.find((p) => p.id === savedProductIdFromGameState);
      if (matchingProduct) {
        setLocalSelectedProduct(matchingProduct);
      }
    }
  }, [fetchedProducts, loadingProducts]);

  return {
    gameState,
    setGameState,
    items,
    setItems,
    loading,
    setLoading,
    claimedPrizes,
    setClaimedPrizes,
    walletAmount,
    setWalletAmount,
    claimedPrizeIds,
    setClaimedPrizeIds,
    appState,
    setAppState,
    selectedProduct: currentSelectedProduct,
    setSelectedProduct: setLocalSelectedProduct,
  };
};
